import { rgbToAciColor } from './aci-colors';
import { DxfWriter } from './dxf-writer';
import { DxfBlock, DxfEntity, DxfExportOptions, DxfPoint } from './types';

const DEFAULT_LAYER = '0';
const DEFAULT_COLOR_INDEX = 7;

/**
 * Points where a bulged segment's arc reaches an axis-aligned extreme, which
 * can lie outside the vertex hull. DXF bulge convention: bulge = tan(θ/4),
 * positive = CCW sweep from a to b (arc bows right of the directed chord).
 */
function bulgeArcExtremes(a: DxfPoint, b: DxfPoint, bulge: number): DxfPoint[] {
  const chordX = b.x - a.x;
  const chordY = b.y - a.y;
  const chordLen = Math.hypot(chordX, chordY);
  if (chordLen < 1e-12 || bulge === 0) return [];

  const radius = (chordLen / 4) * (Math.abs(bulge) + 1 / Math.abs(bulge));
  // Center: chord midpoint offset along the left perpendicular. With the DXF
  // sign (CCW-positive) the apex sits at mid − leftPerp·sagitta and the center
  // radius further on the opposite side (minor arc).
  const perpX = -chordY / chordLen;
  const perpY = chordX / chordLen;
  const sagitta = (chordLen / 2) * bulge;
  const centerOffset = -sagitta + Math.sign(bulge) * radius;
  const cx = (a.x + b.x) / 2 + perpX * centerOffset;
  const cy = (a.y + b.y) / 2 + perpY * centerOffset;

  const startAngle = Math.atan2(a.y - cy, a.x - cx);
  const sweep = 4 * Math.atan(bulge); // CCW-positive
  const TAU = Math.PI * 2;

  const extremes: DxfPoint[] = [];
  for (const k of [0, Math.PI / 2, Math.PI, (3 * Math.PI) / 2]) {
    const travel = sweep >= 0 ? (((k - startAngle) % TAU) + TAU) % TAU : (((startAngle - k) % TAU) + TAU) % TAU;
    if (travel <= Math.abs(sweep)) {
      extremes.push({ x: cx + radius * Math.cos(k), y: cy + radius * Math.sin(k), z: a.z });
    }
  }
  return extremes;
}

interface Bounds {
  min: DxfPoint;
  max: DxfPoint;
}

function emptyBounds(): Bounds {
  return {
    min: { x: Infinity, y: Infinity, z: Infinity },
    max: { x: -Infinity, y: -Infinity, z: -Infinity },
  };
}

function expandBounds(bounds: Bounds, entity: DxfEntity, blockBounds?: Map<string, Bounds>): void {
  const { min, max } = bounds;
  const expand = (p: DxfPoint) => {
    min.x = Math.min(min.x, p.x);
    min.y = Math.min(min.y, p.y);
    min.z = Math.min(min.z, p.z);
    max.x = Math.max(max.x, p.x);
    max.y = Math.max(max.y, p.y);
    max.z = Math.max(max.z, p.z);
  };

  switch (entity.type) {
    case 'POINT':
      expand(entity.position);
      break;
    case 'LINE':
      expand(entity.start);
      expand(entity.end);
      break;
    case 'POLYLINE':
      entity.points.forEach(expand);
      if (entity.bulges) {
        const n = entity.points.length;
        for (let i = 0; i < entity.bulges.length && i < n; i++) {
          const b = entity.points[(i + 1) % n];
          if (i + 1 === n && !entity.closed) break;
          bulgeArcExtremes(entity.points[i], b, entity.bulges[i]).forEach(expand);
        }
      }
      break;
    case 'HATCH':
      entity.outerBoundary.forEach(expand);
      entity.islands.forEach((island) => island.forEach(expand));
      break;
    case 'TEXT':
      expand(entity.position);
      break;
    case 'INSERT': {
      const block = blockBounds?.get(entity.blockName);
      if (!block || block.min.x > block.max.x) break; // unknown or empty block
      // Conservative superset: transform the block bbox's 4 XY corners by
      // scale → rotate → translate; Z from the scaled block Z range.
      const xs = entity.xScale ?? 1;
      const ys = entity.yScale ?? 1;
      const zs = entity.zScale ?? 1;
      const rad = ((entity.rotation ?? 0) * Math.PI) / 180;
      const cos = Math.cos(rad);
      const sin = Math.sin(rad);
      for (const cx of [block.min.x, block.max.x]) {
        for (const cy of [block.min.y, block.max.y]) {
          for (const cz of [block.min.z, block.max.z]) {
            const sx = cx * xs;
            const sy = cy * ys;
            expand({
              x: entity.position.x + sx * cos - sy * sin,
              y: entity.position.y + sx * sin + sy * cos,
              z: entity.position.z + cz * zs,
            });
          }
        }
      }
      break;
    }
  }
}

/** Block names must be plain identifiers for maximum reader compatibility. */
function sanitizeBlockName(name: string): string {
  return name.replace(/[^A-Za-z0-9_-]/g, '_');
}

/** Map every block's original name to a sanitized, collision-free name. */
function buildBlockRenameMap(blocks: DxfBlock[]): Map<string, string> {
  const renames = new Map<string, string>();
  const used = new Set<string>();
  for (const block of blocks) {
    if (renames.has(block.name)) continue; // duplicate definition; first wins
    let candidate = sanitizeBlockName(block.name);
    let suffix = 1;
    while (used.has(candidate)) candidate = `${sanitizeBlockName(block.name)}_${suffix++}`;
    used.add(candidate);
    renames.set(block.name, candidate);
  }
  return renames;
}

/** Per-block local bounds with nested INSERTs folded in (cycle-guarded). */
function computeBlockBounds(blocks: DxfBlock[]): Map<string, Bounds> {
  const byName = new Map(blocks.map((b) => [b.name, b]));
  const bounds = new Map<string, Bounds>();
  const visiting = new Set<string>();

  const compute = (name: string): Bounds => {
    const cached = bounds.get(name);
    if (cached) return cached;
    const result = emptyBounds();
    bounds.set(name, result);
    const block = byName.get(name);
    if (!block || visiting.has(name)) return result;
    visiting.add(name);
    for (const entity of block.entities) {
      if (entity.type === 'INSERT') compute(entity.blockName);
      expandBounds(result, entity, bounds);
    }
    visiting.delete(name);
    return result;
  };

  for (const block of blocks) compute(block.name);
  return bounds;
}

function emitEntity(writer: DxfWriter, entity: DxfEntity, defaultLayer: string): void {
  const layerName = entity.layerName ?? defaultLayer;
  const color = entity.colorAci ?? entity.color;

  switch (entity.type) {
    case 'POINT':
      writer.writePoint(layerName, color, entity.position);
      break;
    case 'LINE':
      writer.writeLine(layerName, color, entity.start, entity.end);
      break;
    case 'POLYLINE':
      writer.writePolyline(layerName, color, entity.points, entity.closed, entity.dashed, entity.bulges);
      break;
    case 'HATCH':
      writer.writeHatch(layerName, color, entity.outerBoundary, entity.islands);
      break;
    case 'TEXT':
      writer.writeText(layerName, color, entity.position, entity.text, entity.height, entity.rotation, entity.hAlign, entity.vAlign);
      break;
    case 'INSERT':
      writer.writeInsert(layerName, color, entity.blockName, entity.position, entity.rotation, entity.xScale, entity.yScale, entity.zScale);
      break;
  }
}

/**
 * Export an array of DXF entities to an AC1015 (AutoCAD 2000) DXF string.
 * Blocks referenced by INSERT entities are passed via options.blocks; INSERTs
 * referencing undefined blocks are dropped with a warning.
 */
export function exportDxf(entities: DxfEntity[], options?: DxfExportOptions): string {
  const defaultLayer = options?.defaultLayerName ?? DEFAULT_LAYER;
  const defaultColorIndex = options?.defaultColor ? rgbToAciColor(options.defaultColor.r, options.defaultColor.g, options.defaultColor.b) : DEFAULT_COLOR_INDEX;

  // Sanitize block names and rewrite all references consistently.
  const renames = buildBlockRenameMap(options?.blocks ?? []);
  const renameEntity = (entity: DxfEntity): DxfEntity | null => {
    if (entity.type !== 'INSERT') return entity;
    const renamed = renames.get(entity.blockName);
    if (!renamed) {
      console.warn(`[DXF] Dropping INSERT referencing undefined block "${entity.blockName}"`);
      return null;
    }
    return { ...entity, blockName: renamed };
  };
  const blocks: DxfBlock[] = [];
  const seenBlockNames = new Set<string>();
  for (const block of options?.blocks ?? []) {
    const name = renames.get(block.name) as string;
    if (seenBlockNames.has(name)) continue;
    seenBlockNames.add(name);
    blocks.push({ ...block, name, entities: block.entities.map(renameEntity).filter((e): e is DxfEntity => e !== null) });
  }
  const modelEntities = entities.map(renameEntity).filter((e): e is DxfEntity => e !== null);

  const blockBounds = computeBlockBounds(blocks);

  // Collect all unique layers (model space + block internals) and extents.
  const layerSet = new Map<string, number>();
  const collectLayer = (entity: DxfEntity) => {
    const layerName = entity.layerName ?? defaultLayer;
    const colorIndex = entity.colorAci ?? (entity.color ? rgbToAciColor(entity.color.r, entity.color.g, entity.color.b) : defaultColorIndex);
    if (!layerSet.has(layerName)) layerSet.set(layerName, colorIndex);
  };

  const bounds = emptyBounds();
  for (const entity of modelEntities) {
    collectLayer(entity);
    expandBounds(bounds, entity, blockBounds);
  }
  for (const block of blocks) block.entities.forEach(collectLayer);

  // Fallback extents if nothing contributed
  if (bounds.min.x > bounds.max.x) {
    bounds.min.x = bounds.min.y = bounds.min.z = 0;
    bounds.max.x = bounds.max.y = bounds.max.z = 0;
  }

  const layers = Array.from(layerSet.entries()).map(([name, colorIndex]) => ({
    name,
    colorIndex,
  }));

  const writer = new DxfWriter();
  writer.allocateBlockRecords(blocks.map((b) => b.name));

  writer.writeHeader(bounds.min, bounds.max, options?.insUnits);
  writer.writeClasses();
  writer.writeTables(layers);

  writer.writeBlocksStart();
  for (const block of blocks) {
    writer.beginBlock(block.name, block.basePoint);
    for (const entity of block.entities) emitEntity(writer, entity, defaultLayer);
    writer.endBlock();
  }
  writer.writeBlocksEnd();

  writer.writeEntitiesStart();
  for (const entity of modelEntities) emitEntity(writer, entity, defaultLayer);
  writer.writeEntitiesEnd();

  writer.writeObjects();
  writer.writeEof();

  return writer.toString();
}
